import type { SupabaseClient } from '@supabase/supabase-js';
import logger from '@/lib/logger';

export const ROOM_NAME_UNSAFE_CHARS = 'ROOM_NAME_UNSAFE_CHARS';
export const ROOM_NAME_UNSAFE_MESSAGE =
  'New call IDs may contain only letters A-Z and a-z, digits 0-9, and hyphens (-).';

/** Interim storage-collision prevention. Underscores can be collision victims too.
 * This is a creation policy, never a rename, sanitizer or existing-call join rule.
 */
export function isSafeChosenRoomName(value: unknown): value is string {
  return typeof value === 'string' && value.length > 0 && !/[^A-Za-z0-9-]/.test(value);
}

export class ChosenRoomNameError extends Error {
  readonly code = ROOM_NAME_UNSAFE_CHARS;
  readonly status = 400;
  constructor() { super(ROOM_NAME_UNSAFE_MESSAGE); this.name = 'ChosenRoomNameError'; }
}

export class RoomNameLookupError extends Error {
  readonly code = 'ROOM_NAME_LOOKUP_FAILED';
  readonly status = 503;
  constructor() { super('Unable to verify whether this call already exists. Please try again.'); this.name = 'RoomNameLookupError'; }
}

export function rejectUnsafeChosenRoomName(roomName: string, route: string): void {
  if (isSafeChosenRoomName(roomName)) return;
  logger.error(route, 'Unsafe new call ID refused', { code: ROOM_NAME_UNSAFE_CHARS });
  throw new ChosenRoomNameError();
}

/** Exact durable row lookup, only for names outside the new alphabet. Callers
 * still enforce ownership separately. On servers pass an admin client: RLS must
 * never hide an existing row and turn a rejoin into a creation decision.
 * Browser writers use their own RLS client; it cannot authorize a hidden row.
 */
export async function assertSafeNewRoomName(
  callId: string,
  db: Pick<SupabaseClient, 'from'>,
  route: string,
  roomName: string = callId,
): Promise<void> {
  if (isSafeChosenRoomName(callId) && isSafeChosenRoomName(roomName)) return;
  let existing: { id: string; room_name?: string } | null;
  try {
    const { data, error } = await db.from('calls').select('id, room_name').eq('call_id', callId).maybeSingle();
    if (error || data === undefined) throw new RoomNameLookupError();
    existing = data;
  } catch {
    logger.error(route, 'Call ID existence lookup failed', { code: 'ROOM_NAME_LOOKUP_FAILED' });
    throw new RoomNameLookupError();
  }
  if (!existing) rejectUnsafeChosenRoomName(callId, route);
  // A separate room_name must not introduce a new unsafe alias, even when
  // call_id is safe. Preserve an existing row's exact stored name too.
  if (existing && (roomName === callId || roomName === existing.room_name)) return;
  rejectUnsafeChosenRoomName(roomName, route);
}
